import { TypedDocumentNode, gql } from '@apollo/client';
import { faCrown } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';

import Button from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { ChevronDownBold } from '@sorare/core/src/atoms/icons/ChevronDownBold';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, BodyS } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { HorizontalDivider } from '@sorare/core/src/atoms/ui/Divider';
import Avatar from '@sorare/core/src/components/user/Avatar';
import { MyClubLink } from '@sorare/core/src/components/user/MyClubLink';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { isType } from '@sorare/core/src/gql';
import useScreenSize from '@sorare/core/src/hooks/device/useScreenSize';
import useAmountWithConversion from '@sorare/core/src/hooks/useAmountWithConversion';
import { zeroMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';
import { monetaryAmountFragment } from '@sorare/core/src/lib/monetaryAmount';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import BidField from 'components/buyActions/BidField';
import { StarBallExponent } from 'components/forYou/RecommendationTitle/StarBallExponent';
import FlexCard from 'components/token/FlexCard';

import { Countdown } from '../Countdown';
import { LoosingBids } from './LoosingBids';
import { StarUniqueAuction_anyCard } from './__generated__/index.graphql';
import gradient from './assets/gradient.jpg';
import mobileGradient from './assets/mobileGradient.jpg';

export const Root = styled.div`
  display: grid;
  grid-template-columns: 1fr max-content 1fr;
  column-gap: var(--double-unit);
  padding: var(--triple-unit) calc(6 * var(--unit));
  width: 100%;
  align-items: flex-start;
  background-size: cover;
  background-position: center;
  justify-content: space-between;
`;

export const MobileRoot = styled(Vertical)`
  padding: var(--double-unit);
  background-size: cover;
  background-position: top;
`;

const CountdownCtn = styled(Vertical).attrs({ gap: 2 })`
  align-items: flex-start;
`;

const BestBid = styled(Horizontal).attrs({ gap: 2 })`
  min-width: 0;
  @media ${laptopAndAbove} {
    text-align: right;
    align-items: flex-start;
    flex-direction: row-reverse;
  }
`;

const Token = styled.div`
  --token-width: var(--card-mobile-width);
  flex-basis: var(--token-width);
  width: var(--token-width);
  min-width: var(--token-width);
  align-self: center;
  transition: all 0.3s;
  @media ${laptopAndAbove} {
    --token-width: calc(12 * var(--unit));
    align-self: flex-start;
  }
  .expanded & {
    @media ${laptopAndAbove} {
      --token-width: 248px;
    }
  }
`;

const DesktopLeft = styled(Vertical)`
  justify-content: space-between;
  height: 100%;
  flex-grow: 0;
  flex-shrink: 0;
  align-items: space-between;
`;

const DesktopRight = styled(Vertical).attrs({ gap: 3 })`
  justify-content: flex-end;
  min-width: 0;
`;

const NoWrap = styled.span`
  white-space: nowrap;
`;
const Metas = styled(Vertical).attrs({ gap: 0 })`
  justify-content: flex-start;
  align-items: flex-start;
  min-width: 0;
  @media ${laptopAndAbove} {
    align-items: flex-end;
    .expanded & {
      font-size: 16px;
    }
  }
`;
const BestBidImage = styled(MyClubLink)`
  width: calc(7 * var(--unit));
  height: calc(7 * var(--unit));
  border-radius: 50%;
  flex-shrink: 0;
  @media ${laptopAndAbove} {
    width: calc(9 * var(--unit));
    height: calc(9 * var(--unit));
  }
`;

const Title = styled.h2`
  font-size: 32px;
  font-weight: normal;
  line-height: 1;
  transition: all 0.3s;
  .expanded & {
    font-size: 56px;
  }
`;

const ShowMoreCtn = styled(Horizontal)`
  justify-content: flex-end;
`;

const StyledChevronDownBold = styled(ChevronDownBold)`
  transition: all 0.3s;
  .expanded & {
    transform: rotate(180deg);
  }
`;

const ellipsis = css`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  max-width: 100%;
  width: 100%;
`;

const Ellipsis = styled(BodyS)`
  ${ellipsis}
`;

const MobileBids = styled(Horizontal).attrs({ gap: 2 })`
  justify-content: space-around;
  width: 100%;
  min-width: 0;
`;

const MobileHorizontal = styled(Horizontal)`
  min-width: 0;
  max-width: 100%;
`;
const MobileVertical = styled(Vertical)`
  min-width: 0;
`;
const Actions = styled(Vertical).attrs({ gap: 2 })`
  max-width: 420px;
  width: 100%;
  min-width: 0;
`;
const Amount = styled(BodyM)`
  ${ellipsis}
`;

type Props = {
  card: StarUniqueAuction_anyCard;
  expanded: boolean;
  onToggle: () => void;
};

export const StarUniqueAuction = ({ card, expanded, onToggle }: Props) => {
  const { formatDistanceToNow } = useIntlContext();
  const { up: isLaptopOrDesktop } = useScreenSize('laptop');
  const auction = card?.latestEnglishAuction;

  const { main } = useAmountWithConversion({
    monetaryAmount: auction?.bestBid?.amounts || zeroMonetaryAmount,
  });

  if (!auction) {
    return null;
  }

  const cardElt = (
    <Token>
      <FlexCard card={card} withLink width={isLaptopOrDesktop ? 320 : 160} />
    </Token>
  );

  const countdown = auction.open ? (
    <Countdown endDate={auction.endDate} expanded={expanded} />
  ) : (
    <>
      <FormattedMessage {...fantasy.ended} />{' '}
      {formatDistanceToNow(auction.endDate)}
    </>
  );

  const bestBid = isType(auction?.bestBid?.bidder, 'User') && (
    <BestBid>
      <BestBidImage user={auction.bestBid.bidder}>
        <Avatar user={auction.bestBid.bidder} variant="responsive" rounded />
      </BestBidImage>
      <Metas>
        <Chip
          size="smaller"
          iconLeft={() => <FontAwesomeIcon icon={faCrown} />}
          label={<FormattedMessage id="dededede" defaultMessage="Top Bid" />}
          custom={{
            background: 'linear-gradient(323deg, #171717 14.3%, #4D4B49 85.7%)',
          }}
        />
        <Ellipsis bold>
          <MyClubLink user={auction.bestBid.bidder}>
            {auction.bestBid.bidder.nickname}
          </MyClubLink>
        </Ellipsis>
        <Amount>{main}</Amount>
      </Metas>
    </BestBid>
  );

  const loosingBids = (
    <LoosingBids auctionId={auction.id} expanded={expanded} />
  );

  if (isLaptopOrDesktop) {
    return (
      <Root
        style={{
          backgroundImage: `url(${gradient})`,
        }}
        className={classNames({ expanded })}
      >
        <DesktopLeft gap={2}>
          <Title>
            <FormattedMessage
              id="SpecialEvents.StarUniqueAuction.title"
              defaultMessage="Unique{br}<noWrap>Auction{sorareBallExponent}</noWrap>"
              values={{
                br: <br />,
                sorareBallExponent: <StarBallExponent />,
                noWrap: chunks => <NoWrap>{chunks}</NoWrap>,
              }}
            />
          </Title>
          <CountdownCtn>
            {countdown}
            {expanded && auction.open && card && (
              <BidField
                auction={auction}
                cards={[card]}
                variant="medium"
                color="secondary"
              />
            )}
          </CountdownCtn>
        </DesktopLeft>
        {cardElt}
        <DesktopRight>
          {bestBid}
          {loosingBids}
          <ShowMoreCtn>
            <Button
              startIcon={<StyledChevronDownBold />}
              size="small"
              color="secondary"
              stroke
              onClick={onToggle}
              disableDebounce
            >
              <FormattedMessage
                {...(expanded ? glossary.showLess : glossary.showMore)}
              />
            </Button>
          </ShowMoreCtn>
        </DesktopRight>
      </Root>
    );
  }

  if (expanded) {
    return (
      <MobileRoot
        style={{
          backgroundImage: `url(${mobileGradient})`,
        }}
        className={classNames({ expanded })}
        center
        gap={2}
      >
        {cardElt}
        {countdown}
        <HorizontalDivider />
        <MobileBids gap={2}>
          {bestBid}
          {loosingBids}
        </MobileBids>
        <Actions>
          {auction.open && card && (
            <BidField
              auction={auction}
              cards={[card]}
              variant="small"
              color="secondary"
            />
          )}
          <Button
            startIcon={<StyledChevronDownBold />}
            color="secondary"
            stroke
            onClick={onToggle}
            size="small"
            disableDebounce
            fullWidth
          >
            <FormattedMessage {...glossary.showLess} />
          </Button>
        </Actions>
      </MobileRoot>
    );
  }

  return (
    <MobileRoot
      style={{
        backgroundImage: `url(${mobileGradient})`,
      }}
      className={classNames({ expanded })}
      center
    >
      <MobileHorizontal gap={4}>
        {cardElt}
        <MobileVertical gap={2}>
          {bestBid}
          {countdown}
          <Button
            startIcon={<StyledChevronDownBold />}
            color="secondary"
            stroke
            onClick={onToggle}
            size="small"
            disableDebounce
            fullWidth
          >
            <FormattedMessage {...glossary.showMore} />
          </Button>
        </MobileVertical>
      </MobileHorizontal>
    </MobileRoot>
  );
};

StarUniqueAuction.fragments = {
  anyCard: gql`
    fragment StarUniqueAuction_anyCard on AnyCardInterface {
      slug
      latestEnglishAuction {
        id
        open
        bestBid {
          id
          amounts {
            ...MonetaryAmountFragment_monetaryAmount
          }
          bidder {
            ... on User {
              slug
              nickname
            }
            ...MyClubLink_publicUserInfoInterface
            ...Avatar_publicUserInfoInterface
          }
        }
        ...BidField_auction
      }
      ...BidField_anyCard
      ...FlexCard_anyCard
    }
    ${FlexCard.fragments.anyCard}
    ${monetaryAmountFragment}
    ${BidField.fragments.anyCard}
    ${BidField.fragments.auction}
    ${MyClubLink.fragments.user}
    ${Avatar.fragments.publicUserInfoInterface}
  ` as TypedDocumentNode<StarUniqueAuction_anyCard>,
};
